import React from 'react';
import { useQuery } from '@apollo/client';
import { Button, Modal, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
// Api
import {
  GET_STREAM_PARTICIPANT_INVITE,
  GET_TICKET_EVENT_PARTICIPANT_INVITE,
} from 'api/ticketing/queries';
// Types
import {
  GetTicketEventParticipantInvite,
  GetTicketEventParticipantInviteVariables,
} from 'api/ticketing/types/GetTicketEventParticipantInvite';
import {
  GetTicketEventStreamInvite,
  GetTicketEventStreamInviteVariables,
} from 'api/ticketing/types/GetTicketEventStreamInvite';
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
// Helpers
import {
  getEventInviteUrl,
  getEventPublicUrl,
  getStreamInviteUrl,
} from 'pages/Events/links';
// UI
import CopyLink from 'ui/CopyLink/CopyLink';
// Styles
import styles from './ShareModal.module.scss';

const { Paragraph } = Typography;

type ShareModalProps = {
  event: TicketEventDetailsFields;
  visible: boolean;
  onClose: () => void;
};

const ShareModal = ({
  event,
  visible,
  onClose,
}: ShareModalProps): JSX.Element => {
  const streamId = event.streamId;

  const { data: eventInvite } = useQuery<
    GetTicketEventParticipantInvite,
    GetTicketEventParticipantInviteVariables
  >(GET_TICKET_EVENT_PARTICIPANT_INVITE, {
    variables: { input: { ticketEventId: event.id } },
    skip: !visible || !!streamId,
  });
  const { data: streamInvite } = useQuery<
    GetTicketEventStreamInvite,
    GetTicketEventStreamInviteVariables
  >(GET_STREAM_PARTICIPANT_INVITE, {
    variables: {
      input: { streamId: streamId || '', storeId: event.hostUserId },
    },
    skip: !visible || !streamId,
  });

  const publicUrl = getEventPublicUrl(event);
  const invite = streamId
    ? streamInvite?.getOrCreateStreamParticipantInviteAdmin.invite
    : eventInvite?.getOrCreateTicketEventParticipantInvite.invite;
  const inviteUrl =
    invite &&
    (streamId
      ? getStreamInviteUrl(streamId, invite)
      : getEventInviteUrl(event.id, invite));

  return (
    <Modal
      title="Share & promote"
      visible={visible}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className={styles.root}>
        {event.coverImageUrl && (
          <div className={styles.poster}>
            <img src={event.coverImageUrl} alt={`${event.name} poster`} />
            <Button
              icon={<DownloadOutlined />}
              href={event.coverImageUrl}
              target="_blank"
              rel="noopener noreferrer"
              download
            >
              Download poster
            </Button>
          </div>
        )}
        {publicUrl ? (
          <CopyLink
            name="event-link"
            label="Event promotion link"
            link={publicUrl}
          />
        ) : (
          <Paragraph type="secondary">
            The event page goes live once the event is published.
          </Paragraph>
        )}
        {inviteUrl && (
          <CopyLink
            name="invite-link"
            label="Participant invitation link"
            link={inviteUrl}
          />
        )}
      </div>
    </Modal>
  );
};

export default ShareModal;
